import React, { useState } from 'react';
import { Calendar, Trash2, Plus } from 'lucide-react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AvailabilityStatus, DateAvailability } from '../types';
interface DateAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  contributorName: string;
  solutionName: string;
  schedules: DateAvailability[];
  onAddSchedule: (schedule: Omit<DateAvailability, 'id'>) => void;
  onDeleteSchedule: (id: string) => void;
}
const STATUS_OPTIONS: {
  value: AvailabilityStatus;
  label: string;
}[] = [{
  value: 'available',
  label: 'Qualified and Available'
}, {
  value: 'limited',
  label: 'Qualified not Available'
}, {
  value: 'unavailable',
  label: 'Not Qualified'
}];
export function DateAvailabilityModal({
  isOpen,
  onClose,
  contributorName,
  solutionName,
  schedules,
  onAddSchedule,
  onDeleteSchedule
}: DateAvailabilityModalProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<AvailabilityStatus>('available');
  const [error, setError] = useState('');
  const handleAdd = () => {
    setError('');
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 60);
    if (start < today) {
      setError('Start date cannot be in the past');
      return;
    }
    if (end < start) {
      setError('End date must be after start date');
      return;
    }
    if (end > maxDate) {
      setError('Cannot schedule more than 60 days in advance');
      return;
    }
    onAddSchedule({
      startDate,
      endDate,
      status
    });
    // Reset form
    setStartDate('');
    setEndDate('');
    setStatus('available');
  };
  // Sort schedules by start date
  const sortedSchedules = [...schedules].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  return <Modal isOpen={isOpen} onClose={onClose} title="Schedule Availability" footer={<div className="flex justify-end" data-id="element-405">
          <Button variant="ghost" onClick={onClose} data-id="element-406">
            Close
          </Button>
        </div>} data-id="element-404">
      
      <div className="space-y-6" data-id="element-407">
        <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-408">
          <p className="text-sm text-slate-600" data-id="element-409">
            Scheduling for{' '}
            <span className="font-semibold text-slate-900" data-id="element-410">
              {contributorName}
            </span>{' '}
            on{' '}
            <span className="font-semibold text-slate-900" data-id="element-411">{solutionName}</span>
          </p>
        </div>

        <div className="space-y-4 border-b border-slate-200 pb-6" data-id="element-412">
          <h4 className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-413">
            <Plus className="h-4 w-4" data-id="element-414" />
            Add New Schedule
          </h4>

          <div className="grid grid-cols-2 gap-4" data-id="element-415">
            <Input type="date" label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} min={new Date().toISOString().split('T')[0]} data-id="element-416" />
            
            <Input type="date" label="End Date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate || new Date().toISOString().split('T')[0]} data-id="element-417" />
            
          </div>

          <div data-id="element-418">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-419">
              Status
            </label>
            <select value={status} onChange={e => setStatus(e.target.value as AvailabilityStatus)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-420">
              
              {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-421">
                  {option.label}
                </option>)}
            </select>
          </div>

          {error && <p className="text-sm text-red-600" data-id="element-422">{error}</p>}

          <Button onClick={handleAdd} className="w-full" data-id="element-423">
            Add Schedule
          </Button>
        </div>

        <div className="space-y-3" data-id="element-424">
          <h4 className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-425">
            <Calendar className="h-4 w-4" data-id="element-426" />
            Upcoming Schedules
          </h4>

          {sortedSchedules.length === 0 ? <p className="text-sm text-slate-500 italic" data-id="element-427">
              No upcoming schedules set.
            </p> : <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-id="element-428">
              {sortedSchedules.map(schedule => <div key={schedule.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-md text-sm" data-id="element-429">
              
                  <div data-id="element-430">
                    <div className="font-medium text-slate-900" data-id="element-431">
                      {new Date(schedule.startDate).toLocaleDateString()} -{' '}
                      {new Date(schedule.endDate).toLocaleDateString()}
                    </div>
                    <div className={`text-xs mt-1 inline-flex items-center px-2 py-0.5 rounded-full ${schedule.status === 'available' ? 'bg-green-100 text-green-700' : schedule.status === 'limited' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-432">
                  
                      {STATUS_OPTIONS.find(o => o.value === schedule.status)?.label}
                    </div>
                  </div>
                  <button onClick={() => onDeleteSchedule(schedule.id)} className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors" title="Delete schedule" data-id="element-433">
                
                    <Trash2 className="h-4 w-4" data-id="element-434" />
                  </button>
                </div>)}
            </div>}
        </div>
      </div>
    </Modal>;
}